import { setLogVerbosity } from '@apollo/client'
import { VendorClaim } from '@skedulo/horizon-types'
import type { IAuthData, IAuthServers } from '@skedulo/horizon-types'
import { grpc } from '@skedulo/protobuf-web'
import { ActionsApiClientV1, ActionsRpcClientV1 } from './actions-api-client'
import { FetchApiClient } from './fetch-api-client'
import { GraphQLApiClient } from './graphql/client'
import { QueryApiClientV1 } from './query-api-client'
import { DataSchemaApiClient } from './schema/client'
import {
  AvatarApiClient,
  RuleViolationsApiClient,
  PolicyEngineApiClient,
} from './services'
import { UiApiClientV1 } from './ui'
import { resolveTenantEnvironment, type TenantEnvironment } from './utils'
import { ViewStateApiClientV1 } from './view-state-api-client'
import type { IApiClientOptions, IAuthSession } from '../types'

setLogVerbosity('silent')

export * from './actions-api-client'
export * from './services'
export * from './query-api-client'
export * from './view-state-api-client'
export * from './ui'
export * from './graphql'
export * from './schema'
export * from './utils'

/**
 * ApiClientV1 is a wrapper around the GRPC clients that are autogenerated from
 * the protobuf (in @skedulo/protobuf-web). This allows us to provide a simpler,
 * more idiomatic API where necessary.
 *
 * Wrapped requests also mean more accurate typing can be enforced. If using the
 * clients out of the box, all properties are optional in requests, but this
 * doesn't always make sense. For example, a PUT request to update an existing
 * object must provide a `uid` so this wrapper can enforce that by expecting a
 * stricter type.
 *
 * @example
 * const apiClient = new ApiClientV1({ platformApiDomain: ..., auth: ... })
 * const response = await apiClient.ui.nav.get({ uid: ... })
 * const graphqlData = await apiClient.graphQL.getClient().query({ query: ... })
 */
export class ApiClientV1 {
  readonly query: QueryApiClientV1
  readonly ui: UiApiClientV1
  readonly actions: ActionsApiClientV1
  readonly actionsRpc: ActionsRpcClientV1
  readonly viewState: ViewStateApiClientV1
  readonly graphQL: GraphQLApiClient
  readonly dataSchema: DataSchemaApiClient
  readonly avatar: AvatarApiClient
  readonly ruleViolations: RuleViolationsApiClient
  readonly policy: PolicyEngineApiClient
  /**
   * A set of FetchApiClient instance based on auth.servers endpoints
   * @example
   * const presignedUrl = await apiClient.services.packagingServer.get<string>(`/package/download/00000000-0000-0000-0000-000000000000`)
   */
  readonly services: Record<keyof IAuthServers, FetchApiClient>

  private readonly auth: IAuthData
  private readonly platformApiDomain: string

  constructor({
    platformApiDomain,
    auth,
    service,
    eventBus,
  }: IApiClientOptions) {
    this.auth = auth
    this.platformApiDomain = platformApiDomain

    const session: IAuthSession = {
      userId: this.userId,
      tenantId: this.tenantId,
    }

    const metadata = new grpc.Metadata({
      Authorization: `Bearer ${auth.skedApiAccessToken}`,
      'service-name': service ?? '',
    })

    this.services = Object.fromEntries(
      Object.entries(auth.servers).map(([key, apiEndpoint]) => [
        key,
        new FetchApiClient(apiEndpoint, auth),
      ]),
    ) as Record<keyof IAuthServers, FetchApiClient>

    this.query = new QueryApiClientV1({ platformApiDomain, metadata, session })
    this.ui = new UiApiClientV1(
      {
        platformApiDomain,
        metadata,
        session,
      },
      this.services.uiServer,
    )
    this.actions = new ActionsApiClientV1(
      new FetchApiClient(auth.servers.apiServer, auth),
    )
    this.actionsRpc = new ActionsRpcClientV1({
      platformApiDomain,
      metadata,
      session,
    })
    this.viewState = new ViewStateApiClientV1(this.services.uiServer)
    this.graphQL = new GraphQLApiClient({ platformApiDomain, eventBus }, auth)
    this.dataSchema = new DataSchemaApiClient(
      this.graphQL,
      new FetchApiClient(auth.servers.apiServer, auth),
    )
    this.avatar = new AvatarApiClient(
      new FetchApiClient(auth.servers.apiServer, auth),
    )
    this.ruleViolations = new RuleViolationsApiClient(
      new FetchApiClient(auth.servers.apiServer, auth),
    )
    this.policy = new PolicyEngineApiClient(
      new FetchApiClient(auth.servers.apiServer, auth),
    )
  }

  /**
   * Exposes a user ID for the current session. This can be useful context for
   * platform components, which don't have direct access to auth.
   */
  get userId() {
    return this.auth.profile['https://api.skedulo.com/ven'].user_id
  }

  /**
   * Exposes a tenant ID for the current session. This can be useful context for
   * platform components, which don't have direct access to auth.
   */
  get tenantId() {
    return this.auth.profile['https://api.skedulo.com/organization_id']
  }

  /**
   * Exposes a check for the vendor of the current session. This can be useful context
   * for template environments, which don't have direct access to auth.
   */
  get isSalesforce() {
    return (
      this.auth.profile['https://api.skedulo.com/vendor'] ===
      VendorClaim.Salesforce
    )
  }

  /**
   * Exposes a check for the vendor of the current session. This can be useful context
   * for template environments, which don't have direct access to auth.
   */
  get isSkedulo() {
    return (
      this.auth.profile['https://api.skedulo.com/vendor'] ===
      VendorClaim.Skedulo
    )
  }

  /**
   * Exposes a check for the vendor of the current session. This can be useful context
   * for template environments, which don't have direct access to auth.
   */
  get isServiceNow() {
    return (
      this.auth.profile['https://api.skedulo.com/vendor'] ===
      VendorClaim.ServiceNow
    )
  }

  /**
   * Exposes the roles for the current session. This can be useful context
   * for template environments, which don't have direct access to auth.
   */
  get roles() {
    return this.auth.profile['https://api.skedulo.com/roles'] ?? []
  }

  /**
   * Exposes the hostname of the configured Platform API domain. Unlike
   * `window.location`, this reflects the environment the client was
   * actually configured to talk to, so it can't drift from the tenant's
   * real environment (e.g. a preview host pointed at any tenant).
   */
  get apiDomain(): string {
    return new URL(this.platformApiDomain).hostname
  }

  /**
   * Exposes the resolved tenant environment, derived from `apiDomain`
   * rather than `window.location`. Useful for consumers that need to pick
   * environment-specific resources (e.g. an analytics catalog) without
   * re-implementing hostname parsing.
   */
  get environment(): TenantEnvironment {
    return resolveTenantEnvironment(this.apiDomain)
  }
}
